/**
 * Alias normalization helpers.
 *
 * Aliases come from many places: admin form (free text), CSV imports, vendor
 * scrapers, search-bar queries. Without a shared canonical form we end up
 * with duplicates that LOOK distinct ("BPC-157" vs "bpc 157" vs "BPC_157")
 * and autocomplete misses obvious matches.
 *
 * Two functions:
 *   - normalizeAliasKey(s): aggressive form used for COMPARISON only
 *     (lowercased, all whitespace/dashes/underscores stripped). This is
 *     the dedupe + lookup key.
 *   - cleanAliasForStorage(s): lighter form used when SAVING. Trims, collapses
 *     internal whitespace, normalizes en/em-dashes to hyphens, but preserves
 *     the user's chosen casing & separator so display stays human-friendly.
 *
 * Rule of thumb: store with cleanAliasForStorage, compare with normalizeAliasKey.
 */

/** Canonical lookup key — strip everything that's not alphanumeric, lowercase. */
export function normalizeAliasKey(input: string): string {
  if (!input) return "";
  return input
    .toLowerCase()
    .normalize("NFKD")
    // Drop combining marks (accented chars → base letter)
    .replace(/[\u0300-\u036f]/g, "")
    // Strip dashes (incl. en/em), underscores, whitespace, dots, slashes
    .replace(/[\s\-\u2010-\u2015_./\\]+/g, "")
    .trim();
}

/** Storage-safe form: keeps casing/separators but cleans junk. */
export function cleanAliasForStorage(input: string): string {
  if (!input) return "";
  return input
    .normalize("NFKC")
    // Collapse en/em dashes & similar to a regular hyphen so we don't store mixed unicode
    .replace(/[\u2010-\u2015]/g, "-")
    // Collapse runs of whitespace to a single space
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Dedupe a list of aliases by normalized key, preserving the FIRST occurrence's
 * display form. Use when saving so the stored array can't contain
 * formatting-only duplicates.
 */
export function dedupeAliases(aliases: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of aliases) {
    if (!raw) continue;
    const display = cleanAliasForStorage(raw);
    if (!display) continue;
    const key = normalizeAliasKey(display);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(display);
  }
  return out;
}

/**
 * Returns true if two strings are equivalent under alias normalization.
 * Use for conflict checks ("does this alias already exist on another compound?").
 */
export function aliasesEquivalent(a: string, b: string): boolean {
  return normalizeAliasKey(a) === normalizeAliasKey(b) && normalizeAliasKey(a) !== "";
}
